/**
 * A class to automatically adjust the height of a textarea based on its content with ability
 * to set a maximum number of rows it can grow to.
 *
 * This technique is more efficient than cloning the textarea and measuring the height of the clone.
 *
 * Notes:
 *  - If the parent element is flex and the initial textarea scrollHeight can be more than the actual scrollHeight
 *  - If textarea height is bigger than rows that is fine and textarea will not shrink
 *
 */
export default class AutoHeightTextarea {
  /**
   * @param {HTMLTextAreaElement} textarea
   * @param {Number} options.maxRows - The maximum number of rows the textarea can grow to. Default is 6.
   */
  constructor(textarea, { maxRows = 6 } = {}) {
    this.textarea = textarea;
    this.maxRows = maxRows;
    this.updateHeight();
    this.textarea.addEventListener('input', this);
    this.initializeResizeObserver();
  }

  initializeResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => this.updateHeight());
    this.resizeObserver.observe(this.textarea);
  }

  handleEvent(e) {
    if (e.type === 'input') this.updateHeight();
  }

  updateHeight() {
    const { textarea, maxRows } = this;

    textarea.rows = 1;
    textarea.style.height = 'auto';
    textarea.style.resize = 'none';

    const c = getComputedStyle(textarea);
    const lineHeight = parseInt(c.lineHeight, 10);
    const padding = parseInt(c.paddingTop, 10) + parseInt(c.paddingBottom, 10);

    const scrollHeightWithoutPadding = textarea.scrollHeight - padding;

    const rows = Math.round(scrollHeightWithoutPadding / lineHeight);

    // console.log('AutoHeightTextarea', { rows: lineHeight, scrollHeightWithoutPadding, padding });

    textarea.rows = Math.min(rows, maxRows);
  }

  destroy() {
    this.textarea.removeEventListener('input', this);
    this.resizeObserver.disconnect();
  }
}
